import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Pagination,
  Spinner,
  Table,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaExclamationCircle } from 'react-icons/fa';
import { Formik } from 'formik';
import Header from '~/Components/Header';
import MaskedInput from 'react-text-mask';
import 'bootstrap/dist/css/bootstrap.min.css';
import api from '~/Services/api';
import { toast } from 'react-toastify';
import format from '~/Utils/format';
import DateMask from '~/Utils/DateMask';
import { MASK_DATE } from '~/constants';
import { salvarEmExcel } from '~/Utils/Helpers';
import * as Yup from 'yup';
import moment from 'moment';
import Select from 'react-select';

export const RelatorioChaves = () => {
  const [dados, setDados] = useState([]);
  const [dadosExcel, setDadosExcel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [departamentos, setDepartamentos] = useState([]);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const opcoesDigitalizacao = [
    { value: null, label: 'Todos' },
    { value: 1, label: 'Sim' },
    { value: 0, label: 'Não' },
  ];

  const totalPages = Math.ceil(dados.length / recordsPerPage);

  const dadosOrdenados = React.useMemo(() => {
    if (sortConfig.key) {
      const sorted = [...dados].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return dados;
  }, [dados, sortConfig]);

  const currentRecords = React.useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return dadosOrdenados.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [dadosOrdenados, currentPage, recordsPerPage]);

  const handleSort = column => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  useEffect(() => {
    const buscarDepartamentos = async () => {
      try {
        setLoadingDepartamentos(true);
        const response = await api.buscarDepartamentos();

        if (response) {
          setDepartamentos([
            { value: '', label: 'Todos' },
            ...response.map(item => ({
              value: item.codigo,
              label: `${item.codigo} - ${item.descricao}`,
            })),
          ]);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoadingDepartamentos(false);
      }
    };

    const buscarUsuarios = async () => {
      try {
        setLoadingUsuarios(true);
        const response = await api.buscarUsuarios();
        if (response) {
          setUsuarios([
            { value: '', label: 'Todos' },
            ...response.map(item => ({
              value: item.codigo,
              label: `${item.codigo} - ${item.nome}`,
            })),
          ]);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoadingUsuarios(false);
      }
    };

    buscarDepartamentos();
    buscarUsuarios();
  }, []);

  const consultarRelatorioChaves = async filtro => {
    try {
      setLoading(true);

      const filtroFormatado = {
        ...filtro,
        data_inicial: filtro.data_inicial
          ? moment(filtro.data_inicial, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : '',
        data_final: filtro.data_final
          ? moment(filtro.data_final, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : '',
      };
      let response = await api.buscarRelatorioChaves(filtroFormatado);

      if (response) {
        setDados(response.analitico);
        setDadosExcel(response);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  };

  const exportarExcel = async () => {
    try {
      setExportandoExcel(true);
      await salvarEmExcel(dadosExcel, '/exportador/excel/relatorio-chaves');
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  const validationSchema = Yup.object().shape({
    data_inicial: Yup.string()
      .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
      .test('valid-date', 'Data inválida', value => {
        if (!value) return true;
        return moment(value, 'DD/MM/YYYY', true).isValid();
      }),
    data_final: Yup.string()
      .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
      .test('valid-date', 'Data inválida', value => {
        if (!value) return true;
        return moment(value, 'DD/MM/YYYY', true).isValid();
      }),
  });

  return (
    <div>
      <Header title="Relatório de Chaves" />
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          departamento: '',
          digitador: '',
          data_inicial: '',
          data_final: '',
          tipo_chave: '',
          digitalizacao_pendente: null,
        }}
        onSubmit={filtro => {
          consultarRelatorioChaves(filtro);
        }}
      >
        {({
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          values,
        }) => (
          <Form
            onSubmit={handleSubmit}
            className="container-fluid bg-white p-3"
          >
            <Row>
              <Col xs={12} sm={6} md={2}>
                <Form.Group controlId="departamento">
                  <Form.Label>Departamento</Form.Label>
                  <Select
                    noOptionsMessage={() => 'Nenhum departamento encontrado'}
                    placeholder="Selecione..."
                    isLoading={loadingDepartamentos}
                    isClearable={true}
                    options={departamentos}
                    value={departamentos.find(
                      option => option.value === values.departamento
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'departamento',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Group controlId="digitador">
                  <Form.Label>Digitador</Form.Label>
                  <Select
                    noOptionsMessage={() => 'Nenhum usuário encontrado'}
                    placeholder="Selecione..."
                    isClearable={true}
                    isLoading={loadingUsuarios}
                    options={usuarios}
                    value={usuarios.find(
                      option => option.value === values.digitador
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'digitador',
                        selectedOption ? selectedOption.value : ''
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Group controlId="digitalizacao_pendente">
                  <Form.Label>Digitalização Pendente</Form.Label>
                  <Select
                    placeholder="Selecione..."
                    options={opcoesDigitalizacao}
                    value={opcoesDigitalizacao.find(
                      option => option.value === values.digitalizacao_pendente
                    )}
                    onChange={selectedOption => {
                      setFieldValue(
                        'digitalizacao_pendente',
                        selectedOption ? selectedOption.value : null
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Label>Data Inicial</Form.Label>
                <DateMask allowEmpty>
                  {() => (
                    <>
                      <div className="position-relative">
                        <MaskedInput
                          name="data_inicial"
                          type="tel"
                          guide={false}
                          className={`form-control ${
                            errors.data_inicial && touched.data_inicial
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="Ex.: 01/01/2019"
                          mask={MASK_DATE}
                          value={values.data_inicial}
                          onChange={e => {
                            setFieldValue('data_inicial', e.target.value);
                          }}
                          onBlur={() => {
                            setFieldTouched('data_inicial', true);
                          }}
                        />
                        {errors.data_inicial && touched.data_inicial && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-data-inicial">
                                {errors.data_inicial}
                              </Tooltip>
                            }
                          >
                            <FaExclamationCircle
                              className="text-danger position-absolute"
                              style={{
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                              }}
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                    </>
                  )}
                </DateMask>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Group>
                  <Form.Label>Data Final</Form.Label>
                  <DateMask allowEmpty>
                    {() => (
                      <>
                        <div className="position-relative">
                          <MaskedInput
                            name="data_final"
                            type="tel"
                            guide={false}
                            className={`form-control ${
                              errors.data_final && touched.data_final
                                ? 'is-invalid'
                                : ''
                            }`}
                            placeholder="Ex.: 01/01/2019"
                            mask={MASK_DATE}
                            value={values.data_final}
                            onChange={e => {
                              setFieldValue('data_final', e.target.value);
                            }}
                            onBlur={() => {
                              setFieldTouched('data_final', true);
                            }}
                          />
                          {errors.data_final && touched.data_final && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="tooltip-data-final">
                                  {errors.data_final}
                                </Tooltip>
                              }
                            >
                              <FaExclamationCircle
                                className="text-danger position-absolute"
                                style={{
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  cursor: 'pointer',
                                }}
                              />
                            </OverlayTrigger>
                          )}
                        </div>
                      </>
                    )}
                  </DateMask>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Group>
                  <Form.Label>&nbsp;</Form.Label>
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100"
                    disabled={
                      values.data_inicial === '' ||
                      values.data_final === '' ||
                      loading ||
                      (errors.data_inicial && touched.data_inicial) ||
                      (errors.data_final && touched.data_final)
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <FaSearch className="mr-2" />
                        Buscar
                      </>
                    )}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {dados.length > 0 ? (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row className="mb-3">
              <Col>
                <span>Total de {dados.length} registro(s) encontrado(s).</span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="success"
                  onClick={exportarExcel}
                  disabled={exportandoExcel}
                >
                  {exportandoExcel ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </div>
          <div className="container-fluid bg-white table-responsive">
            <Table className="table table-sm table-striped table-bordered table-hover">
              <thead>
                <tr className="text-center font-size-14">
                  <th
                    onClick={() => handleSort('ge')}
                    style={{ cursor: 'pointer' }}
                  >
                    GE
                  </th>
                  <th
                    onClick={() => handleSort('chave')}
                    style={{ cursor: 'pointer' }}
                  >
                    Chave
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    Status
                  </th>
                  <th
                    onClick={() => handleSort('digitalizacao_pendente')}
                    style={{ cursor: 'pointer' }}
                  >
                    Dig. Pendente
                  </th>
                  <th
                    onClick={() => handleSort('data_criacao')}
                    style={{ cursor: 'pointer' }}
                  >
                    Data Criação
                  </th>
                  <th
                    onClick={() => handleSort('departamento')}
                    style={{ cursor: 'pointer' }}
                  >
                    Departamento
                  </th>
                  <th
                    onClick={() => handleSort('devolvida')}
                    style={{ cursor: 'pointer' }}
                  >
                    Devolvida
                  </th>
                  <th
                    onClick={() => handleSort('digitador')}
                    style={{ cursor: 'pointer' }}
                  >
                    Digitador
                  </th>
                  <th
                    onClick={() => handleSort('fornecedor')}
                    style={{ cursor: 'pointer' }}
                  >
                    Fornecedor
                  </th>
                  <th
                    onClick={() => handleSort('tipo')}
                    style={{ cursor: 'pointer' }}
                  >
                    Tipo
                  </th>
                  <th
                    onClick={() => handleSort('valor')}
                    style={{ cursor: 'pointer' }}
                  >
                    Valor
                  </th>
                  <th
                    onClick={() => handleSort('usuario_caixa_aprovacao')}
                    style={{ cursor: 'pointer' }}
                  >
                    Cx. Aprovação
                  </th>
                </tr>
              </thead>
              <tbody className="text-center font-size-13">
                {currentRecords.map((item, index) => (
                  <tr
                    key={index}
                    style={{
                      fontWeight: 'normal',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s ease',
                    }}
                    className="text-left"
                    onClick={() => {}}
                  >
                    <td>{item.ge}</td>
                    <td>{item.chave}</td>
                    <td>{item.status}</td>
                    <td>{item.digitalizacao_pendente || 'N'}</td>
                    <td>{format.date(item.data_criacao)}</td>
                    <td>{item.departamento || 'Não Informado'}</td>
                    <td>{item.devolvida || 'Não Informado'}</td>
                    <td>{item.digitador || 'Não Informado'}</td>
                    <td>{item.fornecedor || 'Não Informado'}</td>
                    <td>{item.tipo || 'Não Informado'}</td>
                    <td>{format.money(item.valor) || 'Não Informado'}</td>
                    <td>{item.usuario_caixa_aprovacao || 'Finalizado'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="container-fluid bg-white p-3">
            <Row className="mt-3 d-flex justify-content-between align-items-center">
              <Col xs="auto">
                {totalPages > 1 && (
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </Col>
            </Row>
          </div>
        </>
      ) : (
        !loading && (
          <div className="container-fluid bg-white p-5 mt-3 text-center">
            <Row>
              <Col>
                <FaExclamationCircle size={48} className="text-muted mb-3" />
                <h5 className="text-muted">Nenhum registro encontrado</h5>
                <p className="text-muted">
                  Utilize os filtros acima para buscar os dados.
                </p>
              </Col>
            </Row>
          </div>
        )
      )}
    </div>
  );
};
